import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, ShieldCheck, Zap } from "lucide-react";

const PLAN_ICON = { free: ShieldCheck, pro: Sparkles, enterprise: Zap };

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [stripeMode, setStripeMode] = useState("live");
  const [checkoutFor, setCheckoutFor] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => {
      setPlans(data.plans || []);
      setStripeMode(data.stripe_mode || "live");
    });
  }, []);

  const startCheckout = async (lookup_key) => {
    if (!user) {
      toast.error("Please sign in first");
      return;
    }
    setCheckoutFor(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setCheckoutFor(null);
    }
  };

  const currentPlan = user?.plan || "free";

  return (
    <div className="min-h-screen bg-[#FAFAF7] px-6 py-16 text-slate-900">
      <div className="mx-auto max-w-6xl">
        <div className="mb-14 text-center">
          <div className="overline mb-3 text-[#2563EB]">PRICING</div>
          <h1 className="font-serif text-5xl font-semibold tracking-tight lg:text-6xl">
            Ship your submission,<br />not another spreadsheet.
          </h1>
          <p className="mt-5 mx-auto max-w-xl text-slate-500">
            Start free. Upgrade the moment a founder realizes they need a second project — or a second reviewer.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {plans.map((p) => {
            const Icon = PLAN_ICON[p.code] || ShieldCheck;
            const isCurrent = currentPlan === p.code;
            const isPro = p.code === "pro";
            return (
              <div
                key={p.code}
                data-testid={`plan-card-${p.code}`}
                className={`relative flex flex-col border p-8 transition-all ${
                  isPro
                    ? "border-[#2563EB] bg-gradient-to-b from-[#2563EB]/10 to-transparent"
                    : "border-slate-200 bg-white"
                }`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-6 border border-[#2563EB] bg-[#2563EB] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                    Most Popular
                  </div>
                )}
                <div className="mb-4 flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${isPro ? "text-[#93C5FD]" : "text-slate-500"}`} />
                  <div className="overline text-slate-500">{p.name.toUpperCase()}</div>
                </div>
                <div className="mb-2">
                  <span className="font-serif text-5xl font-semibold">${p.price_usd}</span>
                  {p.price_usd > 0 && <span className="ml-2 text-sm text-slate-500">/month</span>}
                </div>
                <div className="mb-6 text-xs text-slate-500">
                  {p.code === "free" && "For evaluating Qualmitta on your first device."}
                  {p.code === "pro" && "For solo founders and small RA teams."}
                  {p.code === "enterprise" && "For medtech teams with reviewers and multiple projects."}
                </div>
                <ul className="mb-8 space-y-2 text-sm">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-700">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${isPro ? "text-[#93C5FD]" : "text-emerald-400"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button
                    disabled
                    data-testid={`plan-btn-current-${p.code}`}
                    className="mt-auto border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-500"
                  >
                    Your current plan
                  </button>
                ) : p.lookup_key ? (
                  <button
                    data-testid={`plan-btn-upgrade-${p.code}`}
                    onClick={() => startCheckout(p.lookup_key)}
                    disabled={checkoutFor === p.lookup_key}
                    className={`mt-auto inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition-all ${
                      isPro
                        ? "bg-[#2563EB] text-white hover:bg-[#1D4ED8]"
                        : "border border-slate-200 text-white hover:border-[#2563EB]"
                    } disabled:opacity-50`}
                  >
                    {checkoutFor === p.lookup_key ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    Upgrade to {p.name}
                  </button>
                ) : (
                  <div className="mt-auto text-center text-xs text-slate-400">
                    Get started — no card required
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div data-testid="pricing-footer" className="mt-16 border-t border-slate-200 pt-10 text-center text-xs text-slate-400">
          Payments processed by Stripe. Cancel anytime. USD, monthly.
          {stripeMode === "test" && (
            <>
              <br />
              <span data-testid="pricing-test-card-hint">
                Test mode card: <span className="font-mono">4242 4242 4242 4242</span> · any future expiry · any CVC
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
